import { useState } from "react";
import { useData, useDispatch } from "./DataContext.js";
import { setSimParam, getSimParam } from "./utils.js";

import Card from "react-bootstrap/Card";
import Collapse from "react-bootstrap/Collapse"
import Button from "react-bootstrap/Button";

import CardTitle from "./CardTitle.js";
import ButtonGrid from "./ButtonGrid.js";
import MyButton from "./MyButton.js";
import Dialog from "./Dialog.js";
import AmendmentStateNb from "./AmendmentStateNb.js";

export default function SessionPanel ()
{
    const gData = useData();
    const gDispatch = useDispatch();

    const sessionNum = gData.sim[gData.simID].sessionNum;
    const sessionStep = getSimParam(gData, "sessionStep");

    const groupType = gData.group[gData.groupID].type;
    
    const adminDialogs = [];

    const [read, setRead] = useState(true);
    function handleRead () { setRead(!read); }

    
    // AMENDMENT COUNTS

    const amendmentNb = Object
	.entries(gData.amendment)
	.filter(([id, amendment]) => { return amendment.simID === gData.simID && amendment.sessionNum === sessionNum; })
	.map(([id, amendment]) => amendment.state)
	.reduce((nb, state) => { nb[state]++; return nb; }, { "draft": 0, "waiting": 0, "submitted": 0, "accepted": 0, "rejected": 0, "deleted": 0 });

    
    // WRITE STEP FUNCTIONS

    function doWriteStop () { setSimParam(gData, gDispatch, "sessionStep", "present"); }

    // if no submitted amendments
    const [writeStopEmpty, setWriteStopEmpty] = useState(false);
    function showWriteStopEmpty () { setWriteStopEmpty(true); }
    function hideWriteStopEmpty () { setWriteStopEmpty(false); }

    adminDialogs.push(
	<Dialog key="writeStopEmpty" show={writeStopEmpty} hide={hideWriteStopEmpty} title="Terminer la phase de négociation">
	    <p>Aucun amendement n'a encore été déposé.</p>
	    <p>Attendez que les délégations rédigent et déposent leurs amendements, puis validez ces dépôts.</p>
	</Dialog>
    );

    // if waiting amendments
    const [writeStopIncomplete, setWriteStopIncomplete] = useState(false);
    function showWriteStopIncomplete () { setWriteStopIncomplete(true); }
    function hideWriteStopIncomplete () { setWriteStopIncomplete(false); }

    adminDialogs.push(
	<Dialog key="writeStopIncomplete" show={writeStopIncomplete} hide={hideWriteStopIncomplete} confirm={doWriteStop} title="Terminer la phase de négociation">
	    <p>Attention : le dépôt de certains amendements est encore en attente de validation (voir onglet <AmendmentStateNb state="draft"/>). Si vous terminez la <span className="text-uppercase">phase de négociation</span> maintenant, ces amendements ne seront pas soumis au vote lors de cette session.</p>
	    <p>Par ailleurs, il ne sera plus possible de déposer des amendements lors de la <span className="text-uppercase">phase de délibération</span>.</p>
	</Dialog>
    );

    // else confirm
    const [writeStopConfirm, setWriteStopConfirm] = useState(false);
    function showWriteStopConfirm () { setWriteStopConfirm(true); }
    function hideWriteStopConfirm () { setWriteStopConfirm(false); }

    adminDialogs.push(
	<Dialog key="writeStopConfirm" show={writeStopConfirm} hide={hideWriteStopConfirm} confirm={doWriteStop} title="Terminer la phase de négociation">
	    <p>Attention : il ne sera plus possible de déposer des amendements lors de la <span className="text-uppercase">phase de délibération</span>.</p>
	</Dialog>
    );

    // main functions
    function handleWriteStop ()
    {
	if (amendmentNb.submitted === 0) showWriteStopEmpty();
	else if (amendmentNb.waiting > 0) showWriteStopIncomplete();
	else showWriteStopConfirm();
    }


    // PRESENT STEP FUNCTIONS
    function doPresentStop () { setSimParam(gData, gDispatch, "sessionStep", "end"); }
    // TODO : Add button to close simulation (with php endSession.php similar to newSession.php ?)

    // if waiting amendments
    const [presentStopIncomplete, setPresentStopIncomplete] = useState(false);
    function showPresentStopIncomplete () { setPresentStopIncomplete(true); }
    function hidePresentStopIncomplete () { setPresentStopIncomplete(false); }

    adminDialogs.push(
	<Dialog key="presentStopIncomplete" show={presentStopIncomplete} hide={hidePresentStopIncomplete} title="Terminer la phase de délibération">
	    <p>Certains amendements n'ont pas encore été soumis au vote.</p>
	    <p>Sélectionnez chacun des amendements dans l'onglet <AmendmentStateNb state="submitted"/>, puis cliquez sur <span className="text-primary">Lancer la présentation</span> ou <span className="text-primary">Ne pas voter</span>.</p>
	</Dialog>
    );

    // else confirm
    const [presentStopConfirm, setPresentStopConfirm] = useState(false);
    function showPresentStopConfirm () { setPresentStopConfirm(true); }
    function hidePresentStopConfirm () { setPresentStopConfirm(false); }

    adminDialogs.push(
	<Dialog key="presentStopConfirm" show={presentStopConfirm} hide={hidePresentStopConfirm} confirm={doPresentStop} title="Terminer la phase de délibération">
	    <p>Clore la session en cours. Vous pourrez ensuite démarrer un nouvelle <span className="text-uppercase">phase de négociation</span>.</p>
	</Dialog>
    );

    function handlePresentStop ()
    {
	if (amendmentNb.submitted > 0) showPresentStopIncomplete();
	else showPresentStopConfirm();
    }


    // FIRST AND NEW SESSION FUNCTIONS

    function doNewSession ()
    {
	setSimParam(gData, gDispatch, "sessionStep", "write");
	gDispatch({ type: "newSession" });
    }
    
    // confirm first
    const [firstSessionConfirm, setFirstSessionConfirm] = useState(false);
    function showFirstSessionConfirm () { setFirstSessionConfirm(true); }
    function hideFirstSessionConfirm () { setFirstSessionConfirm(false); }

    adminDialogs.push(
	<Dialog key="firstSessionConfirm" show={firstSessionConfirm} hide={hideFirstSessionConfirm} title="Démarrer la simulation" confirm={doNewSession}>
	    <p>La simulation va commencer par une première <span className="text-uppercase">phase de négociation</span>. Les délégations pourront déposer leurs amendements.</p>
	</Dialog>
    );

    // confirm new
    const [newSessionConfirm, setNewSessionConfirm] = useState(false);
    function showNewSessionConfirm () { setNewSessionConfirm(true); }
    function hideNewSessionConfirm () { setNewSessionConfirm(false); }

    adminDialogs.push(
	<Dialog key="newSessionConfirm" show={newSessionConfirm} hide={hideNewSessionConfirm} title="Démarrer une nouvelle session" confirm={doNewSession}>
	    <p>Une nouvelle <span className="text-uppercase">phase de négociation</span> va commencer. Les délégations pourront déposer de nouveaux amendements.</p>
	</Dialog>
    );

    // main function
    function handleFirstSession () { showFirstSessionConfirm(); }
    function handleNewSession () { showNewSessionConfirm(); }

    // elements
    const firstSessionButton = (
	<div className="d-flex align-items-center justify-content-end gap-3">
	    <Button variant="primary" size="sm" onClick={handleFirstSession}>Démarrer la simulation</Button>
	</div>
    );


    // BUTTONS
    const rightButtonList = [];
    if (gData.admin)
    {
	if (sessionStep === "write") rightButtonList.push(<Button key="writeStop" variant="primary" onClick={handleWriteStop}>Terminer la phase de négociation</Button>);
	if (sessionStep === "present") rightButtonList.push(<Button key="presentStop" variant="primary" onClick={handlePresentStop}>Terminer la phase de délibération</Button>);
	if (sessionStep === "end") rightButtonList.push(<Button key="newSession" variant="primary" onClick={handleNewSession}>Démarrer une nouvelle session</Button>);
    }

    // HEADERS
    let stepHeader = "";
    let stepTitle = "";
    let stepDescription = (<></>);
    
    switch (sessionStep)
    {
	case "write" :
	//stepHeader = "Phase A";
	stepHeader = "Négociation et dépôt des amendements";
	switch (groupType)
	{
	    case "delegation" :
	    stepDescription = "Négociez avec les autres délégations. Rédigez et déposez vos amendements qui seront ensuite validés par la Présidence."
	    break;
	    
	    case "lobby" :
	    stepDescription = "Négociez avec les délégations. Proposez leur de déposer certains amendements.";
	    break;

	    case "media" :
	    stepDescription = "Observez les négociations en cours. Rédigez des artiches sur les sujets abordés.";
	    break;

	    case "admin" :
	    stepDescription = "Lisez, puis validez les amendements déposés par les délégations (voir onglet RÉDIGÉ ci-dessous).";
	    break;

	    default:
	}
	break;
	
	case "present" :
	//stepHeader = "Phase B";
	stepHeader = "Délibération et vote des amendements";
	switch (groupType)
	{
	    case "delegation" :
	    stepDescription = "Attendez que la Présidence vous appelle pour présentez vos amendements. Soutenez ou opposez-vous aux amendements présentés par les autres délégations, puis votez POUR ou CONTRE ces amendements, ou abstenez-vous.";
	    break;

	    case "lobby" :
	    stepDescription = "Soutenez ou opposez-vous aux amendements présentés par les délégations.";
	    break;

	    case "media" :
	    stepDescription = "Observez les discussions. Rédigez des articles sur les sujets abordés.";
	    break;

	    case "admin" :
	    stepDescription = "Parcourez la liste des amendements déposés. Sélectionnez un amendement, lancez la présentation, animez les délibérations, puis lancez le vote.";
	    break;

	    default:
	}
	break;

	case "end" :
	stepHeader = "Session terminée";
	break;

	default:
	stepHeader = "Erreur (état non-reconnu)";
    }

    if (sessionNum === 0)
    {
	return (
	    <>
		<div>
		    <div className="text-uppercase text-muted fs-5 pb-2">Vous êtes prêts ?</div>
		    {firstSessionButton}
		</div>
		{adminDialogs}
	    </>
	);
    }
    
    else {
	return (
	    <>
		<Card>
		    <Card.Header>
			<CardTitle subtitle={stepHeader} title={stepTitle}>
			    {/* {sessionStep !== "end" && (<MyButton icon={read ? "readLess" : "readMore"} variant="outline-secondary" onClick={handleRead}/>)} */}
			</CardTitle>
		    </Card.Header>
		    {sessionStep !== "end" && (
			<Collapse in={read}>
			    <div>
				<Card.Body>
				    {stepDescription}
				</Card.Body>
			    </div>
			</Collapse>
		    )}
		    {gData.admin && (
			<Card.Footer>
			    <ButtonGrid>
				<ButtonGrid.Left></ButtonGrid.Left>
				<ButtonGrid.Right>{rightButtonList}</ButtonGrid.Right>
			    </ButtonGrid>
			</Card.Footer>
		    )}
		</Card>
		{adminDialogs}
	    </>
	);
    }
}
